import { API_ENDPOINT } from "~/constants/api-endpoint";
import { useRewardsStore } from "~/stores/rewards-store";
import type {
  ICheckoutResponseData,
  IGetApplicableCouponsResponse,
} from "~/types/checkout";
import type {
  ICityResponse,
  IModuleCountApiResponse,
  IStateCity,
  IStateResponse,
} from "~/types/common";
import type { IOrderSummary } from "~/types/order";
import type { IRewardResponse, RewardsPayload, Reward } from "~/types/rewards";

export const useCommon = () => {
  // Initialize variables
  const otp: Ref<string> = ref("");
  const isLoading: Ref<boolean> = ref(false);
  const isSubmitting: Ref<boolean> = ref(false);
  const isCityLoading: Ref<boolean> = ref(false);
  const isStateLoading: Ref<boolean> = ref(false);
  const errorMessage: Ref<string> = ref("");
  const initTimer: Ref<boolean> = ref(false);
  const timerCountSec: Ref<number> = ref(90);
  const isResendingOTP: Ref<boolean> = ref(false);
  const isTimerShowing: Ref<boolean> = ref(true);
  const rewards = ref<Reward[]>([]);
  const rewardsUrl = API_ENDPOINT.REWARDS;
  let countdownTimeout: ReturnType<typeof setTimeout> | null = null;
  const isRewardsLoading = ref(false);

  // Initialize router, error handling, and snackbar
  const router = useRouter();
  const { handleErrorResponse } = useErrors();
  const { showSnackbar } = useSnackbar();

  // Initialize stores and computed properties
  const rewardsStore = useRewardsStore();
  const { setRewards, setPagination } = useRewardsStore();
  const orderStore = useOrderStore();
  const checkoutStore = useCheckoutStore();
  const orderId = computed(() => checkoutStore.getOrderId);

  // Location data state
  const states = ref<IStateCity[]>([]);
  const cities = ref<IStateCity[]>([]);
  const selectedCountryId = ref("1");

  /**
   * Countdown timer function
   */
  const countDownTimer = (): void => {
    // Check if the timer is initialized
    if (initTimer.value) {
      // Decrement the timer count
      if (timerCountSec.value > 0) {
        timerCountSec.value--;
      }

      // If the timer is still running, set a timeout to decrement the timer again
      if (timerCountSec.value >= 0 && timerCountSec.value > 0) {
        // Set a timeout to decrement the timer again
        countdownTimeout = setTimeout(() => {
          countDownTimer();
        }, 1000);
      } else {
        // If the timer has reached 0, hide the timer
        isTimerShowing.value = false;
      }
    }
  };

  /**
   * Formats the date
   */
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  

  /**
   * Resets the countdown timer
   */
  const resetCountDown = (): void => {
    // Clear the timeout if it exists
    if (countdownTimeout) {
      clearTimeout(countdownTimeout);
    }

    // Reset the timer count and show the timer
    timerCountSec.value = 90;
    isTimerShowing.value = true;

    // Initialize the timer
    initTimer.value = true;

    // Start the timer
    countDownTimer();
  };

  /**
   * Resends the OTP
   */
  const resendOTP = async (): Promise<void> => {
    // Set the resending OTP flag to true
    isResendingOTP.value = true;
    try {
      // Reset the resending OTP flag
      isResendingOTP.value = false;

      // Clear the OTP value
      otp.value = "";

      // Reset the timer
      resetCountDown();
    } catch (err) {
      // Set the resending OTP flag to false
      console.error(err);
      isResendingOTP.value = false;

      // Clear the OTP value
      otp.value = "";

      // Set the error message
      errorMessage.value = "Failed to resend OTP";
    }
  };

  /**
   * Fetches the rewards list based on the provided payload
   * @param payload - The payload containing pagination and filter data
   */
  const getRewardsList = async (payload: RewardsPayload) => {
    try {
      const { data, current_page, last_page } =
        await useApi().get<IRewardResponse>(`${rewardsUrl}`, {
          params: {
            page: payload.page,
            per_page: payload.per_page,
            sort: payload.sort,
            order_by: payload.order_by,
            search: payload.search,
            filter: payload.filters,
          },
        });

      // If the data is available, set the rewards and pagination
      if (data) {
        // Set the rewards and pagination
        rewards.value = data;

        // Set the rewards and pagination
        setRewards(data);

        // Set the pagination
        setPagination({
          currentPage: current_page,
          lastPage: last_page,
        });
      }
    } catch (error: any) {
      // Handle error response
      errorMessage.value = handleErrorResponse(error.response).error;

      // Show the error message
      showSnackbar(errorMessage.value, "error");

      // Reset the rewards
      rewards.value = [];
    }
  };

  /**
   * Fetches the count of all modules
   */
  const getallmodulecount = async () => {
    try {
      // Fetch the module count from the API
      const response = await useApi().get<IModuleCountApiResponse>(
        `${API_ENDPOINT.ALL_MODULE_COUNT}`
      );
      // Set the module count
      rewardsStore.setModuleCount(response.data);
    } catch (error: any) {
      // Handle error response
      errorMessage.value = handleErrorResponse(error.response).error;

      // Show the error message
      showSnackbar(errorMessage.value, "error");
    }
  };

  /**
   * Fetches states list based on country ID
   * Updates states reference data
   */
  const fetchStates = async () => {
    isStateLoading.value = true;
    try {
      // Fetch states list from the API
      const response: IStateResponse = await useApi().get("states", {
        params: { country_id: selectedCountryId.value },
      });
      // Update states reference data
      states.value = response.data;
    } catch (error: any) {
      // Handle error response
      errorMessage.value = handleErrorResponse(error.response).error;

      // Show the error message
      showSnackbar(errorMessage.value, "error");
    } finally {
      // Reset loading state
      isStateLoading.value = false;
    }
  };

  /**
   * Fetches cities list based on selected state
   * @param stateId - ID of the selected state
   */
  const fetchCities = async (stateId: string) => {
    isCityLoading.value = true;
    try {
      // Fetch cities list from the API
      const response: ICityResponse = await useApi().get("cities", {
        params: { state_id: stateId },
      });
      // Update cities reference data
      cities.value = response.data;
    } catch (error: any) {
      // Handle error response
      errorMessage.value = handleErrorResponse(error.response).error;

      // Clear the cities if the state is not found
      if (error.status === 404) {
        cities.value = [];
      }
      showSnackbar(errorMessage.value, "error");
    } finally {
      // Reset loading state
      isCityLoading.value = false;
    }
  };

  /**
   * Fetches the order summary based on the cart ID
   */
  const fetchOrderSummary = async () => {
    try {
      // Fetch the order summary from the API
      const response: IOrderSummary = await useApi().post(
        API_ENDPOINT.ORDER_SUMMARY,
        {
          order_id: orderId.value,
        },
      );

      // Set the order summary
      orderStore.setOrderSummary(response);
    } catch (error: any) {
      // Handle error response
      errorMessage.value = handleErrorResponse(error.response).error;

      // Show the error message
      showSnackbar(errorMessage.value, "error");
    }
  };

  /**
   * Fetches the checkout data
   */
  const checkOut = async () => {
    try {
      // Create checkout session
      const response = await useApi().post<ICheckoutResponseData>(
        API_ENDPOINT.CHECKOUT,
        {
          order_id: orderId.value,
        },
      );
      // Set checkout data
      checkoutStore.setCheckoutData(response.data);
    } catch (error: any) {
      // Handle error response
      errorMessage.value = handleErrorResponse(error.response).error;

      // Show the error message
      showSnackbar(errorMessage.value, "error");
    }
  };

  /**
   * Fetches the coupons
   */
  const fetchCoupons = async () => {
    try {
      // Fetch the coupons
      const response = await useApi().get<IGetApplicableCouponsResponse>(
        API_ENDPOINT.GET_APPLICABLE_COUPONS,
      );

      // Set the applicable coupons to the checkout store
      checkoutStore.setApplicableCoupons(response.data);
    } catch (error: any) {
      // Handle error response
      errorMessage.value = handleErrorResponse(error.response).error;

      // Show the error message
      showSnackbar(errorMessage.value, "error");
    }
  };

  /**
   * Handles the order summary and coupons fetch
   */
  const handleSummaryAndCouponFetch = async () => {
    // Set the loading state
    isLoading.value = true;

    // Fetch the coupons
    if (checkoutStore.hasCoupon) {
      await fetchCoupons();
    }

    // Fetch the order summary
    await fetchOrderSummary();

    // Reset the loading state
    isLoading.value = false;
  };

  return {
    isLoading,
    isSubmitting,
    isCityLoading,
    isStateLoading,
    states,
    cities,
    selectedCountryId,
    errorMessage,
    initTimer,
    otp,
    timerCountSec,
    isResendingOTP,
    isTimerShowing,
    router,
    countDownTimer,
    resetCountDown,
    resendOTP,
    rewards,
    getRewardsList,
    getallmodulecount,
    fetchStates,
    fetchCities,
    checkOut,
    handleSummaryAndCouponFetch,
    fetchOrderSummary,
    isRewardsLoading,
    formatDate,
  };
};
